import { useEffect, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Check, ArrowRight, BookOpen, Home } from "lucide-react";
import { track } from "@/lib/analytics";

/**
 * Lightweight confirmation page Calendly redirects to after a booking.
 *
 * Operator setup: Calendly → Event Settings → Confirmation page → Redirect to:
 *   https://sideb.io/booking-confirmed?email={invitee_email}&service={event_type_name}
 */
export default function BookingConfirmed() {
  const [params] = useSearchParams();
  const service = params.get("service") || "";
  const trackedRef = useRef(false);

  useEffect(() => {
    if (trackedRef.current) return;
    trackedRef.current = true;
    track("booking_confirmed_landing", {
      service,
      has_email: Boolean(params.get("email")),
    });
  }, [service, params]);

  return (
    <div className="max-w-2xl mx-auto px-6 lg:px-10 py-24 text-center" data-testid="booking-confirmed-page">
      <div className="inline-flex w-16 h-16 rounded-full bg-[#ac62ea]/12 items-center justify-center mb-7">
        <Check className="h-8 w-8 text-[#ac62ea]" />
      </div>
      <div className="label-mono text-[#ac62ea]" data-testid="booking-confirmed-label">Confirmed</div>
      <h1 className="font-display text-4xl sm:text-5xl mt-3 tracking-[-0.035em] font-semibold">
        You&rsquo;re booked.
      </h1>
      <p className="text-muted-foreground mt-5 max-w-lg mx-auto leading-relaxed text-lg">
        {service ? <>Your <span className="text-foreground font-medium">{service}</span> is scheduled.</> : "Your appointment is scheduled."}
        {" "}A calendar invite with the meeting details is on its way to your inbox. Talk soon.
      </p>

      <div className="mt-12 flex flex-col sm:flex-row gap-3 justify-center" data-testid="confirmed-cta-row">
        <Button asChild className="rounded-full bg-[#0A0A0A] hover:bg-black/85 h-11 px-6" data-testid="confirmed-home-cta">
          <Link to="/">
            <Home className="h-4 w-4 mr-2" /> Back to home
          </Link>
        </Button>
        <Button asChild variant="outline" className="rounded-full h-11 px-6" data-testid="confirmed-insights-cta">
          <Link to="/articles">
            <BookOpen className="h-4 w-4 mr-2" /> Browse insights <ArrowRight className="h-4 w-4 ml-2" />
          </Link>
        </Button>
      </div>
    </div>
  );
}
